import type { Editor } from 'tinymce';

const ATTACHMENT_SELECTOR = '.email-attachment-item';
const DRAG_THRESHOLD_PX = 5;

// Lets email attachment cards be moved up and down the email body with the mouse. It deliberately
// replaces TinyMCE's own drag & drop (a translucent copy follows the pointer while the original stays
// put): a dark line marks where the card will land instead. The card can only land between the body's
// top-level blocks, so it can never leave the email body.
export const setupAttachmentDragAndDrop = (editor: Editor) => {
  let card: HTMLElement | null = null;
  let start = { x: 0, y: 0 };
  let dragging = false;
  let dropBefore: Element | null = null; // block the card lands before; null = end of the body
  let dropLine: HTMLElement | null = null;
  let suppressClick = false;

  const reset = () => {
    dropLine?.remove();
    dropLine = null;
    card = null;
    dragging = false;
  };

  // Cancels TinyMCE's own ghost drag of non-editable elements (and native link/image drags).
  editor.on('dragstart', (event) => event.preventDefault());

  editor.on('mousedown', (event) => {
    reset();
    const target = event.target as Element;
    if (event.button !== 0 || editor.mode.isReadOnly() || target.closest('.email-attachment-remove')) return;
    card = target.closest<HTMLElement>(ATTACHMENT_SELECTOR);
    start = { x: event.clientX, y: event.clientY };
  });

  editor.on('mousemove', (event) => {
    if (!card) return;
    if (event.buttons === 0) {
      // Released outside the editor, where we never saw the mouseup.
      reset();
      return;
    }
    if (!dragging && Math.hypot(event.clientX - start.x, event.clientY - start.y) < DRAG_THRESHOLD_PX) return;
    dragging = true;

    const blocks = [...editor.getBody().children].filter((el) => el !== card && !el.hasAttribute('data-mce-bogus'));
    dropBefore = blocks.find((el) => event.clientY < el.getBoundingClientRect().top + el.getBoundingClientRect().height / 2) ?? null;
    const edge = dropBefore ? dropBefore.getBoundingClientRect().top : blocks.at(-1)?.getBoundingClientRect().bottom;
    if (edge === undefined) return;

    if (!dropLine) {
      dropLine = editor.getDoc().createElement('div');
      dropLine.setAttribute('data-mce-bogus', 'all');
      editor.getBody().appendChild(dropLine);
    }
    const cardRect = card.getBoundingClientRect();
    const { scrollX, scrollY } = editor.getWin();
    dropLine.style.cssText =
      `position:absolute;height:2px;background:#00122b;pointer-events:none;` +
      `left:${cardRect.left + scrollX}px;width:${cardRect.width}px;top:${edge + scrollY - 1}px;`;
  });

  editor.on('mouseup', () => {
    const moved = card;
    const before = dropBefore;
    const wasDragging = dragging;
    reset();
    if (!moved || !wasDragging) return;

    // The click that follows a drag would otherwise open the file.
    suppressClick = true;
    setTimeout(() => (suppressClick = false));
    if (before === moved.nextElementSibling) return;
    editor.undoManager.transact(() => {
      editor.getBody().insertBefore(moved, before);
    });
    editor.fire('change');
  });

  // Prepended so it runs before the editor's own click handler that opens the attachment.
  editor.on(
    'click',
    (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
};
